import React, { useCallback, useEffect, useMemo } from 'react';
import { useField } from 'react-final-form';
import { observer } from 'mobx-react';
import { TextField } from 'ksnm-common-ui/lib/Form/InputFields';
import { SelectField } from 'ksnm-common-ui/lib/Form/SelectField';
import { createMask } from 'ksnm-common-ui/lib/MaskedInput';

import { usePermission } from '@src/utils/hooks/usePermission';
import { fetchReferences } from '@stores/dictionaryStores/fetchReferences';
import { frequenciesStore } from '@stores/dictionaryStores/frequenciesStore';
import { monthsStore } from '@stores/dictionaryStores/monthsStore';
import { periodsStore } from '@stores/dictionaryStores/periodsStore';
import { reportGroupsStore } from '@stores/dictionaryStores/reportGroupsStore/reportGroupsStore';
import { reportPeriodEditStore } from '@stores/dictionaryStores/reportPeriodsStore';

function getDaysInMonth(month: number) {
  if (month === 2) return 28; // для февраля високосный не учитываем

  const date = new Date();
  return new Date(date.getFullYear(), month, 0).getDate();
}

export const Form = observer(() => {
  const { isEditMode } = reportPeriodEditStore;
  const { reportGroups } = reportGroupsStore;
  const { periods } = periodsStore;
  const { months } = monthsStore;
  const { frequencies } = frequenciesStore;

  const permissionFrequency = usePermission('CmFrequency');
  const permissionMonth = usePermission('CmMonth');

  useEffect(() => {
    fetchReferences(['reportGroups', 'periods', 'months', 'frequencies', 'reportPeriods']);
  }, []);

  const {
    input: { value: preparationMonthCode },
  } = useField('preparationMonthCode');

  const {
    input: { onChange: changePreparationDay },
  } = useField('preparationDay');

  const {
    input: { value: disclosureMonthCode },
  } = useField('disclosureMonthCode');

  const {
    input: { onChange: changeDueDay },
  } = useField('dueDay');

  const onChangePreparationMonthCode = useCallback(() => {
    changePreparationDay(undefined);
  }, [changePreparationDay]);

  const onChangeDisclosureMonthCode = useCallback(() => {
    changeDueDay(undefined);
  }, [changeDueDay]);

  const renderName = useCallback((value) => `${value.code} - ${value.name}`, []);
  const preparationDayMask = useMemo(() => createMask('NUMBER'), []);
  const dueDayMask = useMemo(() => createMask('NUMBER'), []);

  const preparationMonthDays = useMemo(
    () => (preparationMonthCode ? getDaysInMonth(preparationMonthCode) : 31),
    [preparationMonthCode],
  );
  const disclosureMonthDays = useMemo(
    () => (disclosureMonthCode ? getDaysInMonth(disclosureMonthCode) : 31),
    [disclosureMonthCode],
  );

  return (
    <>
      <SelectField
        items={reportGroups}
        name="reportGroupCode"
        label="Код группы отчетных форм"
        disabled={!isEditMode}
        size="small"
        groupStyle={!isEditMode}
        renderName={renderName}
        allowClear
        required
      />
      <SelectField
        items={periods}
        name="periodCode"
        label="Код периода"
        disabled={!isEditMode}
        size="small"
        groupStyle={!isEditMode}
        renderName={renderName}
        allowClear
        required
      />
      <SelectField
        items={months}
        name="preparationMonthCode"
        label="Код месяца подачи отчетности"
        disabled={!isEditMode || !permissionMonth.VIEW}
        size="small"
        groupStyle={!isEditMode}
        renderName={renderName}
        allowClear
        required
        onChange={onChangePreparationMonthCode}
      />
      <TextField
        name="preparationDay"
        label="День месяца подачи отчетности"
        disabled={!isEditMode}
        size="small"
        groupStyle={!isEditMode}
        maxLength={2}
        min={1}
        max={preparationMonthDays}
        mask={preparationDayMask}
        required
        placeholder={`Введите значение от 1 до ${preparationMonthDays}`}
      />
      <SelectField
        items={months}
        name="disclosureMonthCode"
        label="Месяц раскрытия отчетности"
        disabled={!isEditMode || !permissionMonth.VIEW}
        size="small"
        groupStyle={!isEditMode}
        renderName={renderName}
        allowClear
        required
        onChange={onChangeDisclosureMonthCode}
      />
      <TextField
        name="dueDay"
        label="День месяца раскрытия отчетности"
        disabled={!isEditMode}
        size="small"
        groupStyle={!isEditMode}
        maxLength={2}
        min={1}
        max={disclosureMonthDays}
        mask={dueDayMask}
        required
        placeholder={`Введите значение от 1 до ${disclosureMonthDays}`}
      />
      <SelectField
        items={frequencies}
        name="frequencyCode"
        label="Периодичность"
        disabled={!isEditMode || !permissionFrequency.VIEW}
        size="small"
        groupStyle={!isEditMode}
        renderName={renderName}
        allowClear
      />
    </>
  );
});
